"use client";

import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowUp01Icon } from "@hugeicons-pro/core-solid-rounded";
import type { CmsPhraseFragment } from "@workspace/casino_core/fragments/CmsPhraseFragment";
import type { CmsBottombarFragment } from "@workspace/casino_core/queries/CmsBottombarQuery";
import {
  cn,
  Tabs,
  TabsList,
  TabsTrigger,
  useSidebar,
} from "@workspace/frontend-ui";
import type { FC, ReactNode } from "react";
import { useEffect, useState } from "react";
import NavigationItem from "./NavigationItem";

export type BottomNavigationBarProps = {
  brand: string;
  productType: "casino" | "sports";
  sections?: CmsBottombarFragment;
  phrases: CmsPhraseFragment[];
  onSection?: (item: string) => void;
  onMenu?: () => void;
  className?: string;
  initialToggleState?: boolean;
  classNameToggle?: string;
  currentSection?: string;
  children?: ReactNode;
  isShowCase?: boolean;
  forceExpanded?: boolean;
};

export const BottomNavigationBar: FC<BottomNavigationBarProps> = (props) => {
  const [expanded, setExpanded] = useState<boolean>(() => {
    const initialExpanded = props.initialToggleState ?? true;
    return initialExpanded;
  });
  const { openMobile } = useSidebar();

  useEffect(() => {
    const nextExpanded = props.initialToggleState ?? true;
    if (expanded !== nextExpanded) {
      setExpanded(nextExpanded);
    }
  }, [props.initialToggleState]);

  const enabledProducts = props.sections?.enabledProducts ?? ["casino"];

  const sections =
    (props.productType === "casino"
      ? props.sections?.casinoItems
      : props.sections?.sportsItems) || [];
  const movement =
    expanded || props.forceExpanded ? "" : "transform translate-y-full";

  const handleToggleExpanded = () => {
    if (expanded) {
      setExpanded(false);
      return;
    }

    setExpanded(true);
  };

  if (!enabledProducts.includes(props.productType) || sections.length === 0) {
    // Product is disabled or bar is not configured
    return null;
  }

  return (
    <>
      <div
        className={cn(
          "transition-all duration-200 z-50",
          "fixed bottom-22 top-auto w-10 h-14 right-2",
          "bg-card rounded-4xl border border-border flex",
          "justify-center items-center",
          !props.isShowCase && "md:hidden",
          props.forceExpanded && "hidden!",
          openMobile && "hidden",
          movement,
          props.classNameToggle,
        )}
        onClick={handleToggleExpanded}
      >
        <HugeiconsIcon
          icon={ArrowUp01Icon}
          className={cn(
            "size-5 text-muted-foreground transition-transform duration-200",
            expanded && "rotate-180",
          )}
        />
      </div>
      <Tabs
        value={props.currentSection}
        onValueChange={props.onSection}
        className={cn(
          "fixed pb-10 -bottom-10 min-h-16 left-1/2 transform -translate-x-1/2 z-50",
          "w-full bg-card rounded-t-4xl",
          "transition-all duration-200 border border-border",
          !props.isShowCase && "md:hidden",
          movement,
          props.className,
        )}
      >
        <TabsList
          className="grid grid-flow-col auto-cols-fr items-center gap-2 w-full !h-auto px-3.5 pt-2.5 pb-2"
          variant="ghost"
        >
          {sections.map((item, index) => (
            <TabsTrigger
              key={item.slug}
              asChild
              variant="ghost"
              value={item.slug ?? `section-${index}`}
            >
              <NavigationItem
                brand={props.brand}
                item={item}
                testId={`bottom-bar-item-${index + 1}`}
                onMenu={props.onMenu}
              />
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </>
  );
};

export default BottomNavigationBar;
